import { platformApiClient } from '@/services/platform-api-client';

// ── Asset types ────────────────────────────────────────────────────────────────

export type AssetStatus = 'ACTIVE' | 'SOFT_DELETED' | 'QUARANTINED' | 'PERMANENTLY_DELETED';

export interface AssetRecord {
  id: string;
  storageKey: string;
  currentVersionNumber: number;
  originalFileName: string;
  mimeType: string;
  fileSizeBytes: number;
  checksumSha256: string;
  moduleId: string;
  entityType: string;
  entityId: string | null;
  assetClass: string;
  assetStatus: AssetStatus;
  thumbnailId: string | null;
  isDedupCopy: boolean;
  providerId: string;
  tenantId: string | null;
  uploadedById: string;
  createdAt: string;
}

export interface StorageStatsDto {
  totalObjects: number;
  /**
   * Decimal STRING. `file_size_bytes` is a BigInt column and the server used to
   * return a JS BigInt, which `JSON.stringify` cannot serialise — the endpoint
   * answered HTTP 500 and the dashboard rendered zeros. A string is also exact
   * above `Number.MAX_SAFE_INTEGER`, which a platform-wide total can reach.
   */
  totalBytes: string;
  objectsByStatus: Record<string, number>;
  objectsByModule: Record<string, number>;
}

/** Catalogued (logical) totals for one provider. Physical usage is separate. */
export interface ProviderCataloguedUsageDto {
  providerId: string;
  name: string;
  driverId: string;
  isEnabled: boolean;
  isActive: boolean;
  connectionStatus: string;
  lastHealthCheck: string | null;
  activeAssets: number;
  totalAssets: number;
  /** Decimal string, for the same reason as `StorageStatsDto.totalBytes`. */
  cataloguedBytes: string;
}

// ── Provider management types ──────────────────────────────────────────────────

export interface ProviderListItemDto {
  id: string;
  driverId: string;
  name: string;
  description: string | null;
  driverVersion: string;
  isEnabled: boolean;
  isActive: boolean;
  connectionStatus: string;
  lastHealthCheck: string | null;
  lastHealthError: string | null;
  /**
   * Whether an implementation for `driverId` is registered in the backend
   * StorageDriverRegistry — i.e. whether this provider could be activated.
   * Resolved live by the API; never a frontend constant. Owner decision
   * OQ-PR-1 = A. See docs/platform-designs/PROVIDERS_DESIGN.md §0.4 D-1.
   */
  driverRegistered: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigEntryResponse {
  key: string;
  value: string;
  isSecret: boolean;
  isMasked: boolean;
}

export interface ConfigSchemaEntry {
  key: string;
  label: string;
  isSecret: boolean;
  required: boolean;
  placeholder?: string;
}

export interface ProviderDetailDto extends ProviderListItemDto {
  config: ConfigEntryResponse[];
  configSchema: ConfigSchemaEntry[];
}

export interface ConfigEntryInput {
  key: string;
  value: string;
  isSecret: boolean;
}

export interface DetailedTestChecks {
  directoryExists: boolean;
  writePermission: boolean;
  readPermission: boolean;
  deletePermission: boolean;
  availableSpaceBytes: number | null;
  totalSpaceBytes: number | null;
}

export interface DetailedTestResult {
  success: boolean;
  latencyMs: number;
  error?: string;
  checks: DetailedTestChecks;
}

// ── Health types (for StorageHealthPage) ────────────────────────────────────────

export interface ProviderHealthDto {
  id: string;
  name: string;
  driverId: string;
  isActive: boolean;
  connectionStatus: string;
  lastHealthCheck: string | null;
  lastHealthError: string | null;
}

export interface DiskStatsDto {
  availableSpaceBytes: number | null;
  totalSpaceBytes: number | null;
  usedSpaceBytes: number | null;
  usagePercent: number | null;
}

export interface HealthStatusDto {
  status: 'healthy' | 'degraded' | 'offline';
  activeProvider: ProviderHealthDto | null;
  diskStats: DiskStatsDto | null;
  checkedAt: string;
}

export interface AlertDto {
  severity: 'critical' | 'warning' | 'info';
  message: string;
  providerId?: string;
  detectedAt: string;
}

// ── Envelope unwrap helpers ────────────────────────────────────────────────────
//
// The backend ResponseInterceptor wraps every success response as
// { data: T, meta: {...} } — but `platformApiClient` ALREADY UNWRAPS IT
// (platform-api-client.ts, response interceptor). By the time these helpers run,
// `resp.data` is the payload itself, never the envelope.
//
// These helpers previously performed a SECOND `.data` access. On a populated
// response that read `.data` off an array, produced `undefined`, and — because
// unwrapArray coerces rather than throws — returned []. The Providers page then
// rendered its empty state while the backend was returning all seven providers
// with HTTP 200 (content-length 3157). Diagnosis:
// docs/reports/verification/PROVIDERS_PAGE_EMPTY_STATE_DIAGNOSIS_20260829.md
//
// CLAUDE.md names this exact anti-pattern ("WRONG — double-unwrap; returns
// undefined") and prescribes the shape used below, which is also what the
// sibling client platform-storage.api.ts has always done correctly.
//
// Regression coverage: ame-platform-api-unwrap.test.ts drives the REAL axios
// instance and the REAL interceptor, so a reintroduced double-unwrap fails.

function unwrap<T>(axiosData: unknown): T {
  return axiosData as T;
}

function unwrapArray<T>(axiosData: unknown): T[] {
  return Array.isArray(axiosData) ? (axiosData as T[]) : [];
}

// ── Provider management API ────────────────────────────────────────────────────

export async function listProviders(): Promise<ProviderListItemDto[]> {
  const resp = await platformApiClient.get('/platform/ame/providers');
  return unwrapArray<ProviderListItemDto>(resp.data);
}

export async function getProvider(id: string): Promise<ProviderDetailDto> {
  const resp = await platformApiClient.get(`/platform/ame/providers/${id}`);
  return unwrap<ProviderDetailDto>(resp.data);
}

export async function getProviderConfig(id: string): Promise<ConfigEntryResponse[]> {
  const resp = await platformApiClient.get(`/platform/ame/providers/${id}/config`);
  return unwrapArray<ConfigEntryResponse>(resp.data);
}

export async function getProviderConfigSchema(id: string): Promise<ConfigSchemaEntry[]> {
  const resp = await platformApiClient.get(`/platform/ame/providers/${id}/config/schema`);
  return unwrapArray<ConfigSchemaEntry>(resp.data);
}

export async function updateProviderConfig(id: string, entries: ConfigEntryInput[]): Promise<void> {
  await platformApiClient.put(`/platform/ame/providers/${id}/config`, { entries });
}

export async function enableProvider(id: string): Promise<ProviderListItemDto> {
  const resp = await platformApiClient.patch(`/platform/ame/providers/${id}/enable`);
  return unwrap<ProviderListItemDto>(resp.data);
}

export async function disableProvider(id: string): Promise<ProviderListItemDto> {
  const resp = await platformApiClient.patch(`/platform/ame/providers/${id}/disable`);
  return unwrap<ProviderListItemDto>(resp.data);
}

export async function activateProvider(id: string): Promise<ProviderListItemDto> {
  const resp = await platformApiClient.post(`/platform/ame/providers/${id}/activate`);
  return unwrap<ProviderListItemDto>(resp.data);
}

export async function testProviderDetailed(id: string): Promise<DetailedTestResult> {
  const resp = await platformApiClient.post(`/platform/ame/providers/${id}/test`);
  return unwrap<DetailedTestResult>(resp.data);
}

// ── Health API (used by StorageHealthPage) ────────────────────────────────────

export async function getOverallHealth(): Promise<HealthStatusDto> {
  const resp = await platformApiClient.get('/platform/ame/health');
  return unwrap<HealthStatusDto>(resp.data);
}

export async function getProviders(): Promise<ProviderHealthDto[]> {
  const resp = await platformApiClient.get('/platform/ame/health/providers');
  return unwrapArray<ProviderHealthDto>(resp.data);
}

export async function checkProvider(
  id: string,
): Promise<{ success: boolean; latencyMs: number; error?: string }> {
  const resp = await platformApiClient.post(`/platform/ame/health/providers/${id}/check`);
  return unwrap<{ success: boolean; latencyMs: number; error?: string }>(resp.data);
}

export async function getAlerts(): Promise<AlertDto[]> {
  const resp = await platformApiClient.get('/platform/ame/health/alerts');
  return unwrapArray<AlertDto>(resp.data);
}

// ── Asset explorer API ────────────────────────────────────────────────────────

export interface AssetListParams {
  tenantId?: string;
  moduleId?: string;
  assetClass?: string;
  status?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

/**
 * List assets for the platform Asset Explorer.
 *
 * ── THE DOUBLE-UNWRAP DEFECT THIS FIXES ────────────────────────────────────
 * The controller returns `{ data: assets[], nextCursor }` and the
 * `ResponseInterceptor` wraps it to `{ data: { data: assets[], nextCursor } }`.
 * The `platformApiClient` response interceptor then removes the OUTER envelope,
 * so `resp.data` is already `{ data: assets[], nextCursor }` — exactly one
 * `.data` away from the array.
 *
 * The previous implementation took `.data` TWICE: `resp.data.data` (the array),
 * then `.data` on that array, which is `undefined`. `Array.isArray(undefined)`
 * is false, so it returned `[]` unconditionally — the Explorer could never show
 * an asset, no matter what the database held. This is the exact hazard
 * `CLAUDE.md` warns about under "Response Envelope + Axios Interceptor
 * Contract".
 *
 * `nextCursor` was also discarded, so pagination could not advance; it is now
 * returned via `listAssetsPage`.
 */
export async function listAssets(params: AssetListParams): Promise<AssetRecord[]> {
  return (await listAssetsPage(params)).assets;
}

export interface AssetPage {
  assets: AssetRecord[];
  nextCursor: string | null;
}

/** The paginated form: the cursor is what makes "Load more" work. */
export async function listAssetsPage(params: AssetListParams): Promise<AssetPage> {
  const resp = await platformApiClient.get('/platform/ame/assets', { params });
  const payload = resp.data as { data?: unknown; nextCursor?: unknown } | null | undefined;
  const assets = payload?.data;
  return {
    assets: Array.isArray(assets) ? (assets as AssetRecord[]) : [],
    nextCursor: typeof payload?.nextCursor === 'string' ? payload.nextCursor : null,
  };
}

/**
 * Catalogued storage per provider, for the dashboard breakdown.
 *
 * Providers come from the backend registry — nothing here knows the name of any
 * particular provider, so one added later appears automatically.
 */
export async function listProviderCataloguedUsage(): Promise<ProviderCataloguedUsageDto[]> {
  const resp = await platformApiClient.get('/platform/ame/assets/providers/usage');
  return unwrapArray<ProviderCataloguedUsageDto>(resp.data);
}

export async function getAssetStats(): Promise<StorageStatsDto> {
  const resp = await platformApiClient.get('/platform/ame/assets/stats');
  return unwrap<StorageStatsDto>(resp.data);
}

export async function getAsset(id: string): Promise<AssetRecord> {
  const resp = await platformApiClient.get(`/platform/ame/assets/${id}`);
  return unwrap<AssetRecord>(resp.data);
}

export async function getAssetSignedUrl(id: string): Promise<string> {
  const resp = await platformApiClient.get(`/platform/ame/assets/${id}/signed-url`);
  // Controller returns { url } → ResponseInterceptor wraps to { data: { url }, meta }
  const inner = unwrap<{ url: string }>(resp.data);
  return inner.url;
}

export async function restoreAsset(id: string): Promise<AssetRecord> {
  const resp = await platformApiClient.post(`/platform/ame/assets/${id}/restore`);
  return unwrap<AssetRecord>(resp.data);
}
